import { createElement, useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { safeFailureMessage } from '../safe-failure.js';
import { safeHttpsExternalUrl, validRecommendedInstantFormPreview } from '../recommended-form-preview.js';
import {
  COMPANY_META_SETUP_ENDPOINT,
  companySetupResumeStage,
  consumeCompanyMetaSetupLaunch,
  prefillSetupSelection,
  rememberCompanyMetaSetupLaunch,
  safeMetaTermsUrl,
  setupCheckpointPayload,
  setupEditor,
  setupResourceChoices,
  setupRevisionPayload,
} from '../company-meta-setup.js';
import { curatedMetaAuthorizationPresentation, curatedMetaAuthorizationUsable } from './CuratedMetaConnection.jsx';
import Beacon from './Beacon.jsx';
import Select from './Select.jsx';
import './company-meta-setup.css';

// OGC-527: the one shared Meta setup modal (prototype v15) for Company,
// campaign creation and self-service onboarding. It sequences the existing
// setup contracts (checkpoint, Page verification/assignment, exact Terms,
// profile, preview, confirmation, Form operation polling, completion, repair)
// and never invents evidence: each completed state comes from a response.

const tenant = (companyId) => ({ headers: { 'x-company-id': companyId } });
const ENDPOINT = COMPANY_META_SETUP_ENDPOINT;
const STEPS = Object.freeze([
  Object.freeze({ label: 'Page & account', description: 'Your business identity' }),
  Object.freeze({ label: 'Lead access', description: 'Page terms check' }),
  Object.freeze({ label: 'Review form', description: 'Confirm & save' }),
]);
export const META_SETUP_BUSY_LABELS = Object.freeze({
  checkpoint: 'Saving progress…',
  authorize: 'Opening Meta…',
  assignment: 'Assigning Page…',
  access: 'Checking lead access…',
  terms: 'Checking Meta…',
  preview: 'Preparing your preview…',
  replacement: 'Preparing your preview…',
  create: 'Starting form creation…',
  save: 'Saving setup…',
  complete: 'Verifying setup…',
  repair: 'Preparing lead access…',
  check: 'Checking connection…',
  cancel: 'Discarding proposed setup…',
  refresh: 'Refreshing Meta assets…',
});
// Provider failures that need the existing same-account authorization renewal.
const RENEW_CODES = new Set(['meta_reauthorization_required', 'meta_permissions_insufficient', 'meta_recommended_form_permissions_required']);
const RENEWAL_STATES = new Set(['reauth_required', 'disconnected']);
const CHECK_STATES = new Set(['unknown', 'temporarily_unavailable', 'unhealthy', 'checking', 'unavailable']);
export const META_SETUP_COMPLETION = Object.freeze({
  company: Object.freeze({ cta: 'Back to Company', note: 'These assets are ready for your next campaign.' }),
  campaign: Object.freeze({ cta: 'Continue to targeting', note: 'Next: choose who your campaign will reach. No ads have launched.' }),
  onboarding: Object.freeze({ cta: 'Create your first campaign', note: 'Next: choose the ads for your first campaign. No ads have launched.' }),
});
const FORM_QUESTION = 'When would you like to start your renovation project?';
const FORM_OPTIONS = Object.freeze(['As soon as possible', '1–3 months', '3–6 months', 'Just exploring options']);
const FORM_FIELDS = Object.freeze(['Full name', 'Email address', 'Phone number']);
const FOCUSABLE = 'button:not([disabled]),a[href],input:not([disabled]),select:not([disabled]),summary,[tabindex="0"]';

// A styled disclosure (custom +/− or chevron affordance, no native marker).
export function Disclosure({ className, summary, summaryClassName = '', children, ...props }) {
  return createElement('details', { className, ...props }, createElement('summary', { className: summaryClassName || undefined }, summary), children);
}

function PageAvatar({ label, small = false }) {
  return <span className={`bl-ms-page-avatar${small ? ' is-small' : ''}`} aria-hidden="true">{(String(label || '').trim()[0] || 'M').toUpperCase()}<span>f</span></span>;
}

function CheckTag({ children }) {
  return <span className="bl-ms-tag">✓ {children}</span>;
}

export function MetaInstantFormPreview({ pageLabel, preview = null }) {
  const question = preview?.questions?.[0];
  const options = question?.options?.map((option) => option.label) || FORM_OPTIONS;
  const fields = preview?.questions?.slice(1).map((item) => item.label) || FORM_FIELDS;
  return (
    <section className="bl-ms-form-preview" aria-label="Instant Form preview">
      <div className="bl-ms-preview-header">
        <PageAvatar label={pageLabel} small />
        <div><strong>{pageLabel || 'Facebook Page'}</strong><small>Instant Form preview</small></div>
        <span className="bl-ms-tag is-neutral">Standard form</span>
      </div>
      <div className="bl-ms-preview-content">
        <h3>{question?.label || FORM_QUESTION}</h3>
        <div className="bl-ms-timing-options">{options.map((label) => <div key={label}><span className="bl-ms-radio-sample" aria-hidden="true" />{label}</div>)}</div>
        <div className="bl-ms-contact-preview">{fields.map((label) => <div key={label}><span>{label}</span><i aria-hidden="true" /></div>)}</div>
        {preview ? (
          <Disclosure className="bl-ms-optional" summary="Consent, privacy & thank-you screen">
            <div className="bl-ms-detail-copy">
              <h4>{preview.consent?.title}</h4>
              <p>{preview.consent?.body}</p>
              <p className="bl-ms-consent-preview"><span className="bl-ms-checkbox-sample" aria-hidden="true" />{preview.consent?.checkbox?.text}</p>
              <small>Required · Not preselected</small>
              <p>{preview.privacyPolicy?.linkText}<br /><span className="bl-ms-url-text">{preview.privacyPolicy?.url}</span></p>
              <hr />
              <h4>{preview.thankYou?.headline}</h4>
              <p>{preview.thankYou?.body}</p>
              <p><strong>{preview.thankYou?.buttonText}</strong><br /><span className="bl-ms-url-text">{preview.thankYou?.followUpUrl}</span></p>
              <small>Developer app: {preview.developerAppName} · {preview.locale === 'EN_US' ? 'English (US)' : preview.locale}</small>
            </div>
          </Disclosure>
        ) : <p className="bl-ms-field-note">Includes a required consent checkbox and your privacy policy.</p>}
      </div>
    </section>
  );
}

export function MetaSetupModal({
  entry = 'company',
  companyId,
  companyName,
  initialSnapshot,
  resources,
  canManage,
  metaConnection,
  onAuthorize,
  onCheckAuthorization,
  onRefresh,
  onReload,
  surface = 'company',
  campaignId = '',
  requestApi,
  onSnapshot,
  onClose,
  onComplete,
  connectStep = false,
  existingCampaigns = 0,
  confirmChange = false,
}) {
  const [snapshot, setSnapshot] = useState(initialSnapshot);
  const [editor, setEditor] = useState(() => {
    const value = setupEditor(initialSnapshot);
    return { ...value, selection: prefillSetupSelection(resources, value.selection, { preserveForm: Boolean(initialSnapshot.pending?.operationId || initialSnapshot.pending?.confirmedAt) }) };
  });
  // An already confirmed candidate resumes verification; editable setup
  // (including Change setup) opens at Page & account with the saved selection.
  const [stage, setStage] = useState(() => (companySetupResumeStage(initialSnapshot) === 3 ? 3 : 0));
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [errorCode, setErrorCode] = useState('');
  const [terms, setTerms] = useState(null);
  const [preview, setPreview] = useState(null);
  const [existingForm, setExistingForm] = useState(null);
  const [pollExhausted, setPollExhausted] = useState(false);
  const [verifyFailed, setVerifyFailed] = useState(false);
  const [formCommitted, setFormCommitted] = useState(false);
  const [resumeGeneration, setResumeGeneration] = useState(0);
  const [disconnectedAtStart, setStartedDisconnected] = useState(connectStep === true);
  const [changeConfirmOpen, setChangeConfirmOpen] = useState(confirmChange === true);
  const [announcement, setAnnouncement] = useState('');
  const [discardConfirm, setDiscardConfirm] = useState(false);
  const current = useRef(true);
  const lock = useRef(false);
  const dirty = useRef(JSON.stringify(editor.selection) !== JSON.stringify(setupEditor(initialSnapshot).selection));
  const termsHandoff = useRef(false);
  const autoResumed = useRef(false);
  const autoCompleted = useRef('');
  const snapshotRef = useRef(snapshot);
  const editorRef = useRef(editor);
  const confirmOpenRef = useRef(changeConfirmOpen);
  const dialog = useRef(null);
  const confirmBox = useRef(null);
  const title = useRef(null);
  const content = useRef(null);
  const keyboardHandler = useRef(null);
  const confirmedCandidate = useRef(initialSnapshot.pending?.confirmedAt || initialSnapshot.pending?.operationId ? initialSnapshot.pending.id : '');
  const ids = useId().replaceAll(':', '');
  snapshotRef.current = snapshot; editorRef.current = editor; confirmOpenRef.current = changeConfirmOpen;

  const choices = setupResourceChoices(resources, editor.selection);
  const authorizationState = metaConnection?.status === 'error' ? 'unavailable'
    : ['loading', 'idle'].includes(metaConnection?.status) ? 'loading'
      : curatedMetaAuthorizationPresentation(metaConnection?.payload).state;
  const authorizationUsable = curatedMetaAuthorizationUsable(metaConnection?.payload);
  const connected = authorizationUsable || !['missing', 'loading'].includes(authorizationState);
  const hasOperation = Boolean(snapshot.pending?.operationId);
  const pendingReady = !snapshot.pending && Boolean(snapshot.active?.id) && snapshot.active.id === confirmedCandidate.current;
  const formReuse = existingForm?.state === 'compatible';
  const replacementRequired = existingForm?.state === 'replacement_required';
  const reviewValid = validRecommendedInstantFormPreview(preview, { ...editor, allowExistingFormName: formReuse });
  const renewal = RENEWAL_STATES.has(authorizationState);
  const selectedPage = choices.pages.find((item) => item.resourceId === editor.selection.pageResourceId);
  const selectedAccount = choices.accounts.find((item) => item.resourceId === editor.selection.adAccountResourceId);
  const pageLabel = selectedPage?.label || snapshot.pending?.destination?.page?.label || snapshot.active?.destination?.page?.label || '';
  const termsAccepted = terms?.accepted === true;
  const waitingForForm = hasOperation && !formCommitted;
  const verifyingFailed = pollExhausted || verifyFailed;
  // The Connect step stays in the progress indicator once shown (prototype v15).
  const startedDisconnected = disconnectedAtStart || authorizationState === 'missing';
  const totalSteps = startedDisconnected ? 4 : 3;
  const stepLabel = (index) => `Step ${index + 1 + (startedDisconnected ? 1 : 0)} of ${totalSteps}`;
  const companyLabel = companyName || 'Company';

  const view = pendingReady ? 'success'
    : stage === 3 ? 'verifying'
      : authorizationState === 'missing' ? 'connect'
        : authorizationState === 'loading' ? 'loading'
          : renewal || RENEW_CODES.has(errorCode) ? 'access'
            : CHECK_STATES.has(authorizationState) ? 'check'
              : ['assets', 'terms', 'form'][stage];

  useEffect(() => { if (authorizationState === 'missing') setStartedDisconnected(true); }, [authorizationState]);

  const announce = useCallback((message) => { if (current.current) setAnnouncement(message); }, []);

  useEffect(() => {
    current.current = true;
    const invoker = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const handleKey = (event) => keyboardHandler.current?.(event);
    const handleFocus = (event) => {
      const box = confirmOpenRef.current ? confirmBox.current : dialog.current;
      if (box && !box.contains(event.target)) (box.querySelector('[tabindex="-1"]') || box).focus?.({ preventScroll: true });
    };
    document.addEventListener('keydown', handleKey);
    document.addEventListener('focusin', handleFocus);
    return () => {
      current.current = false;
      document.removeEventListener('keydown', handleKey);
      document.removeEventListener('focusin', handleFocus);
      document.body.style.overflow = previousOverflow;
      if (invoker?.isConnected) invoker.focus?.({ preventScroll: true });
    };
  }, []);

  useEffect(() => {
    if (!dirty.current && !snapshot.pending) setEditor((value) => { const next = { ...value, selection: prefillSetupSelection(resources, value.selection) }; editorRef.current = next; return next; });
  }, [resources, snapshot.pending]);

  const acceptSnapshot = useCallback((next) => {
    if (!current.current || !next) return;
    snapshotRef.current = next; setSnapshot(next); onSnapshot(next);
  }, [onSnapshot]);

  async function action(name, work) {
    // `undefined` means skipped (another request holds the lock); `null` failed.
    if (lock.current || !canManage || !current.current) return undefined;
    lock.current = true; setBusy(name); setError(''); setErrorCode('');
    announce(META_SETUP_BUSY_LABELS[name] || 'Saving progress…');
    try { return await work(); }
    catch (failure) {
      if (current.current) {
        setError(safeFailureMessage(failure, 'integration'));
        setErrorCode(typeof failure?.code === 'string' ? failure.code : '');
        // A conflict, or any failure after confirmation was sent (the server may
        // have sealed the candidate before failing), reloads current revisions.
        if (failure?.status === 409 || ['create', 'save'].includes(name)) await reloadAfterFailure();
      }
      return null;
    } finally { lock.current = false; if (current.current) setBusy(''); }
  }

  // Reload current revisions and require a fresh review. A candidate the
  // server already confirmed resumes verification instead of editing; unsaved
  // destination text is otherwise retained and nothing confirms from stale state.
  async function reloadAfterFailure() {
    const next = await onReload().catch(() => null);
    if (!next || !current.current) return;
    acceptSnapshot(next);
    setPreview(null); setTerms(null); setExistingForm(null);
    if (next.pending?.confirmedAt || next.pending?.operationId) {
      confirmedCandidate.current = next.pending.id;
      dirty.current = false; setFormCommitted(false); setStage(3);
      return;
    }
    if (!next.pending && next.active?.id && next.active.id === confirmedCandidate.current) { dirty.current = false; return; }
    const value = setupEditor(next);
    const selection = prefillSetupSelection(resources, value.selection);
    const kept = { ...value, selection, privacyPolicyUrl: editorRef.current.privacyPolicyUrl, followUpUrl: editorRef.current.followUpUrl };
    setEditor(kept); editorRef.current = kept;
    dirty.current = JSON.stringify(kept) !== JSON.stringify(value);
  }

  async function checkpoint() {
    const next = await requestApi.patch(`${ENDPOINT}/checkpoint`, setupCheckpointPayload(snapshotRef.current, editorRef.current), tenant(companyId));
    if (!current.current) return null;
    acceptSnapshot(next); dirty.current = false;
    return next;
  }

  const checkpointIfNeeded = () => (dirty.current || !snapshotRef.current.pending ? checkpoint() : snapshotRef.current);

  function updateEditor(update) {
    setEditor((previous) => { const next = update(previous); editorRef.current = next; return next; });
  }

  function editSelection(field, value) {
    dirty.current = true; setPreview(null); setExistingForm(null); setTerms(null); setError(''); setErrorCode('');
    updateEditor((previous) => {
      let selection = { ...previous.selection, [field]: value };
      if (field === 'adAccountResourceId') {
        const account = choices.accounts.find((item) => item.resourceId === value);
        // Another credential's account invalidates every dependent choice.
        if (account?.credentialId !== selection.credentialId) selection = { ...selection, credentialId: account?.credentialId || '', pageResourceId: '', instantFormResourceId: '', instagramIdentityResourceId: '' };
      }
      if (field === 'pageResourceId') selection = { ...selection, instantFormResourceId: '', instagramIdentityResourceId: '' };
      return { ...previous, selection: prefillSetupSelection(resources, selection) };
    });
  }

  function editUrl(field, value) {
    dirty.current = true; setPreview(null); setError(''); setErrorCode('');
    updateEditor((previous) => ({ ...previous, [field]: value }));
  }

  // Only the explicit completion action continues the entry flow; closing a
  // finished setup saves the result without navigating anywhere.
  function finish({ advance = true } = {}) { onComplete(snapshotRef.current, { advance }); }

  async function close() {
    if (lock.current) return;
    if (pendingReady) { finish({ advance: false }); return; }
    const sealed = Boolean(snapshotRef.current.pending?.confirmedAt || snapshotRef.current.pending?.operationId);
    if (dirty.current && !hasOperation && !sealed) {
      const saved = await action('checkpoint', checkpoint);
      if (!saved) return;
    }
    onClose({ saved: Boolean(snapshotRef.current.pending) });
  }

  function keepCurrentSetup() {
    // Cancelling Change setup leaves the active setup untouched: no checkpoint.
    setChangeConfirmOpen(false);
    onClose({ saved: false });
  }

  function continueChange() {
    setChangeConfirmOpen(false);
    setTimeout(() => (content.current?.querySelector('.bl-select-trigger:not([disabled])') || title.current)?.focus?.(), 0);
  }

  function containFocus(event) {
    const box = confirmOpenRef.current ? confirmBox.current : dialog.current;
    if (event.key === 'Escape') {
      event.preventDefault(); event.stopPropagation();
      if (confirmOpenRef.current) keepCurrentSetup(); else close();
      return;
    }
    if (event.key !== 'Tab' || !box) return;
    const elements = [...box.querySelectorAll(FOCUSABLE)].filter((element) => element.getClientRects().length);
    const first = elements[0]; const last = elements[elements.length - 1];
    if (!first) { event.preventDefault(); return; }
    if (event.shiftKey && (document.activeElement === first || !elements.includes(document.activeElement))) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && (document.activeElement === last || !box.contains(document.activeElement))) { event.preventDefault(); first.focus(); }
  }
  keyboardHandler.current = containFocus;

  async function connect() {
    await action('authorize', async () => {
      if (dirty.current && !await checkpoint()) return;
      rememberCompanyMetaSetupLaunch({ companyId, surface, campaignId, connectStep: startedDisconnected || authorizationState === 'missing' });
      try { await onAuthorize?.(); }
      catch (failure) {
        // A handoff that never started must not reopen setup on a later visit.
        consumeCompanyMetaSetupLaunch({ companyId, surface, campaignId });
        throw failure;
      }
    });
  }

  async function checkTermsFor(next) {
    const result = await requestApi.post(`${ENDPOINT}/terms`, setupRevisionPayload(next), tenant(companyId));
    if (current.current) setTerms({ ...result, setupId: next.pending.id, revision: next.pending.revision });
    return result;
  }

  function applyTerms(result) {
    if (!current.current || !result) return;
    if (result.accepted === true) { setStage(2); announce('Lead Ads Terms accepted.'); }
    else { setStage(1); announce('Accept Lead Ads Terms for this Page in Meta.'); }
  }

  async function continueFromAssets() {
    const assigned = choices.assigned;
    const verificationRequired = choices.verificationRequired;
    await action(assigned ? 'access' : 'assignment', async () => {
      const page = editorRef.current.selection.pageResourceId;
      let next = assigned ? await checkpointIfNeeded() : await checkpoint();
      if (!next) return;
      if (!assigned) {
        // The explicit click names this exact Page and company. Required access
        // verification precedes the confirmed assignment.
        const binding = { pageResourceId: page, companySetupId: next.pending.id, expectedSetupRevision: next.pending.revision };
        if (verificationRequired) await requestApi.post('/api/saas/meta/page-assignment-verifications', binding, tenant(companyId));
        await requestApi.post('/api/saas/meta/page-assignments', { ...binding, confirmed: true }, tenant(companyId));
        await onReload();
        if (!current.current) return;
        next = snapshotRef.current;
      }
      applyTerms(await checkTermsFor(next));
    });
  }

  async function checkTerms({ silent = false } = {}) {
    await action('terms', async () => {
      const next = await checkpointIfNeeded();
      if (!next) return;
      const result = await checkTermsFor(next);
      applyTerms(result);
      if (current.current && result.accepted !== true && !silent) setError('Meta has not confirmed acceptance yet. Open Meta and accept the terms for this Page.');
    });
  }

  // A saved, already-assigned candidate resumes at its next unfinished action.
  useEffect(() => {
    if (autoResumed.current || initialSnapshot.active || stage !== 0 || !resources || !connected || CHECK_STATES.has(authorizationState) || renewal) return;
    autoResumed.current = true;
    if (companySetupResumeStage(initialSnapshot) === 1 && choices.assigned) checkTerms({ silent: true });
  }, [resources, connected, authorizationState]);

  useEffect(() => {
    function resumed() {
      if (!termsHandoff.current || stage !== 1 || lock.current || document.visibilityState === 'hidden') return;
      termsHandoff.current = false;
      checkTerms();
    }
    window.addEventListener('focus', resumed);
    document.addEventListener('visibilitychange', resumed);
    return () => { window.removeEventListener('focus', resumed); document.removeEventListener('visibilitychange', resumed); };
  }, [stage, companyId]);

  async function reviewForm({ replacement = false } = {}) {
    const invalid = !safeHttpsExternalUrl(editorRef.current.privacyPolicyUrl) ? 'privacy' : !safeHttpsExternalUrl(editorRef.current.followUpUrl) ? 'followup' : '';
    if (invalid) {
      setError('Enter a public HTTPS URL for both destinations before continuing.'); setErrorCode('');
      setPreview(null); setExistingForm(null);
      setTimeout(() => document.getElementById(`${ids}-${invalid}`)?.focus(), 0);
      return;
    }
    await action(replacement ? 'replacement' : 'preview', async () => {
      if (replacement) {
        // A replacement drops the outdated managed Form from the candidate and
        // needs a fresh preview plus explicit confirmation.
        updateEditor((previous) => ({ ...previous, selection: { ...previous.selection, instantFormResourceId: '' } }));
        editorRef.current = { ...editorRef.current, selection: { ...editorRef.current.selection, instantFormResourceId: '' } };
        dirty.current = true; setPreview(null); setExistingForm(null);
      }
      const profile = snapshotRef.current.profile || {};
      if ((profile.privacyPolicyUrl || '') !== editorRef.current.privacyPolicyUrl || (profile.followUpUrl || profile.websiteUrl || '') !== editorRef.current.followUpUrl) {
        // Company URLs are saved to the profile first; the checkpoint then binds
        // the resulting profile revision to this candidate.
        const saved = await requestApi.patch(`${ENDPOINT}/profile`, { expectedRevision: profile.revision ?? 0, privacyPolicyUrl: editorRef.current.privacyPolicyUrl, followUpUrl: editorRef.current.followUpUrl }, tenant(companyId));
        if (!current.current) return;
        acceptSnapshot(saved); dirty.current = true;
      }
      const next = await checkpointIfNeeded();
      if (!next) return;
      const checked = await checkTermsFor(next);
      if (!current.current) return;
      if (checked.accepted !== true) { applyTerms(checked); setError('Meta has not confirmed acceptance yet. Open Meta and accept the terms for this Page.'); return; }
      const result = await requestApi.post(`${ENDPOINT}/preview`, setupRevisionPayload(next), tenant(companyId));
      if (result.existingForm?.state === 'replacement_required') {
        if (current.current) { setPreview(null); setExistingForm(result.existingForm); announce('This company needs an updated lead form.'); }
        return;
      }
      const template = result.preview || (typeof result.template === 'object' ? result.template : result);
      const reviewed = { ...template, templateDigest: template.templateDigest || result.templateDigest };
      if (!validRecommendedInstantFormPreview(reviewed, { ...editorRef.current, allowExistingFormName: result.existingForm?.state === 'compatible' })) {
        throw Object.assign(new Error('Invalid Form review'), { code: 'meta_recommended_form_template_mismatch' });
      }
      if (current.current) { setPreview(reviewed); setExistingForm(result.existingForm || null); announce('Review the setup, then finish.'); }
    });
  }

  async function confirmSetup() {
    if (!reviewValid || replacementRequired) return;
    await action(formReuse ? 'save' : 'create', async () => {
      confirmedCandidate.current = snapshotRef.current.pending?.id || '';
      const next = await requestApi.post(`${ENDPOINT}/confirm`, setupRevisionPayload(snapshotRef.current, { expectedActiveSetupId: snapshotRef.current.active?.id || null, confirmed: true, templateDigest: preview.templateDigest }), tenant(companyId));
      if (!current.current) return;
      acceptSnapshot(next); setStage(3); setPollExhausted(false); setVerifyFailed(false); setFormCommitted(false);
      if (next.pending?.operationId) announce('Your approved form is being created in Meta.');
    });
  }

  async function repair() {
    await action('repair', async () => {
      const next = await requestApi.post(`${ENDPOINT}/repair`, setupRevisionPayload(snapshotRef.current, { confirmed: true }), tenant(companyId));
      acceptSnapshot(next); await onReload();
      if (current.current) announce('Lead access preparation was checked. Check again to finish.');
    });
  }

  // `/setup/complete` resumes an uncommitted saved operation under its original
  // idempotency key (202, still pending) or verifies and activates the candidate.
  async function complete() {
    const next = await action('complete', async () => {
      const response = await requestApi.post(`${ENDPOINT}/complete`, setupRevisionPayload(snapshotRef.current), tenant(companyId));
      acceptSnapshot(response);
      return response;
    });
    if (!current.current || next === undefined) return;
    if (!next) { setVerifyFailed(true); return; }
    setVerifyFailed(false); setPollExhausted(false);
    if (next.pending?.operationId) {
      // Still creating the same Form: keep polling the saved operation.
      if (next.operation?.lifecycle !== 'committed') setFormCommitted(false);
      setResumeGeneration((value) => value + 1);
    } else if (next.pending) setVerifyFailed(true);
  }

  function checkAgain() {
    setError(''); setErrorCode(''); setVerifyFailed(false);
    complete();
  }

  useEffect(() => {
    const operationId = snapshot.pending?.operationId;
    if (!operationId || stage !== 3) return undefined;
    let active = true; let timer; let attempt = 0;
    async function poll() {
      try {
        const operation = await requestApi.get(`/api/saas/meta/recommended-form-operations/${encodeURIComponent(operationId)}`, tenant(companyId));
        if (!active || !current.current) return;
        if (operation.complete === true && operation.lifecycle === 'committed') {
          // "Form created" is shown only from the saved operation result; Ready
          // still waits for exact verification and activation.
          setFormCommitted(true);
          lock.current = true; setBusy('complete'); announce('Form created. Verifying lead access…');
          try {
            const next = await requestApi.post(`${ENDPOINT}/complete`, setupRevisionPayload(snapshotRef.current), tenant(companyId));
            if (active) acceptSnapshot(next);
            // A candidate that stays pending needs an explicit retry, never a silent wait.
            if (active && current.current && next?.pending) setVerifyFailed(true);
          } catch (failure) {
            if (active && current.current) { setError(safeFailureMessage(failure, 'integration')); setErrorCode(failure?.code || ''); setVerifyFailed(true); }
          } finally { lock.current = false; if (current.current) setBusy(''); }
          return;
        }
        if (operation.failed === true) { setError(safeFailureMessage({ code: operation.outcome }, 'integration')); setPollExhausted(true); return; }
        if (++attempt >= 30) { setPollExhausted(true); return; }
        timer = setTimeout(poll, 2000);
      } catch (failure) { if (active) { setError(safeFailureMessage(failure, 'integration')); setPollExhausted(true); } }
    }
    timer = setTimeout(poll, 1000);
    return () => { active = false; clearTimeout(timer); };
  }, [snapshot.pending?.operationId, stage, companyId, requestApi, acceptSnapshot, resumeGeneration]);

  useEffect(() => { if (pendingReady) announce('Meta setup is complete. No ads were launched.'); }, [pendingReady, announce]);

  // A confirmed candidate without a saved operation (an interrupted dispatch or
  // reuse activation) resumes once through /setup/complete; failures then offer
  // Check again, targeted repair or discarding the candidate.
  useEffect(() => {
    const candidate = snapshot.pending;
    const key = `${candidate?.id}:${candidate?.revision}`;
    if (stage !== 3 || pendingReady || !candidate || candidate.operationId || verifyFailed || autoCompleted.current === key) return undefined;
    let timer;
    // Wait for the request that revealed the sealed candidate to release its lock.
    const run = () => {
      if (!current.current) return;
      if (lock.current) { timer = setTimeout(run, 50); return; }
      autoCompleted.current = key;
      complete();
    };
    run();
    return () => clearTimeout(timer);
  }, [stage, pendingReady, snapshot.pending?.id, snapshot.pending?.revision, snapshot.pending?.operationId, verifyFailed]);

  async function discardCandidate() {
    await action('cancel', async () => {
      const next = await requestApi.post(`${ENDPOINT}/cancel`, setupRevisionPayload(snapshotRef.current, { expectedActiveSetupId: snapshotRef.current.active?.id || null, confirmed: true }), tenant(companyId));
      acceptSnapshot(next);
      if (current.current) onClose({ saved: false });
    });
  }

  const reviewed = Boolean(preview);
  useEffect(() => {
    if (confirmOpenRef.current) { confirmBox.current?.querySelector('button')?.focus(); return; }
    if (content.current) content.current.scrollTop = 0;
    title.current?.focus({ preventScroll: true });
  }, [view, reviewed, replacementRequired]);

  function back() {
    if (lock.current) return;
    setError(''); setErrorCode('');
    if (stage === 2 && (preview || replacementRequired)) { setPreview(null); setExistingForm(null); return; }
    if (stage === 2) { setStage(termsAccepted ? 0 : 1); return; }
    setStage((value) => Math.max(0, value - 1));
  }

  function goToStep(index) {
    if (lock.current || index >= stage || stage === 3) return;
    setError(''); setErrorCode(''); setPreview(null); setExistingForm(null); setStage(index);
  }

  const formLabel = preview?.formName || snapshot.pending?.destination?.instantForm?.label || snapshot.active?.destination?.instantForm?.label || 'BeyondLeads standard form';
  const activeSelection = snapshot.active?.selection || {};
  const setupChanged = Boolean(snapshot.active) && (
    ['adAccountResourceId', 'pageResourceId'].some((key) => (activeSelection[key] || '') !== (editor.selection[key] || ''))
    || editor.privacyPolicyUrl !== (snapshot.active.privacyPolicyUrl || '') || editor.followUpUrl !== (snapshot.active.followUpUrl || ''));

  const heading = (eyebrow, text, intro) => <>
    <p className="bl-ms-eyebrow">{eyebrow}</p>
    <h2 className="bl-ms-step-title" tabIndex={-1} ref={title}>{text}</h2>
    <p className="bl-ms-intro" id={`${ids}-intro`}>{intro}</p>
  </>;

  let body;
  if (view === 'success') {
    const page = snapshot.active?.destination?.page?.label || pageLabel;
    const form = snapshot.active?.destination?.instantForm?.label || formLabel;
    body = <div className="bl-ms-success-view">
      <div className="bl-ms-success-mark" aria-hidden="true">✓</div>
      <p className="bl-ms-eyebrow">Setup complete</p>
      <h2 className="bl-ms-step-title" tabIndex={-1} ref={title}>You’re ready for the next step.</h2>
      <p className="bl-ms-intro" id={`${ids}-intro`}>Your Meta assets are saved for future campaigns.</p>
      {existingCampaigns > 0 && <p className="bl-ms-field-note">Existing campaigns keep their saved Meta assets.</p>}
      <div className="bl-ms-success-assets"><div><span>Facebook Page</span><strong>{page || 'Saved Facebook Page'}</strong></div><div><span>Lead form</span><strong>{form}</strong></div></div>
      <p className="bl-ms-approval-note">{(META_SETUP_COMPLETION[entry] || META_SETUP_COMPLETION.company).note}</p>
    </div>;
  } else if (view === 'verifying') {
    const attention = verifyingFailed && !waitingForForm;
    body = <>
      {heading('Finishing setup',
        verifyingFailed ? (waitingForForm ? 'Check saved progress' : 'One check needs another try') : (waitingForForm ? 'Creating your lead form' : 'Getting everything ready'),
        verifyingFailed ? (waitingForForm ? 'The creation request was interrupted. Check saved progress to continue.' : 'Your form is saved. We couldn’t finish checking lead access.')
          : (waitingForForm ? 'Your approved form is being created in Meta.' : 'We’re checking your form and its connection to BeyondLeads.'))}
      <div className="bl-ms-progress-checks">
        <div>{waitingForForm ? <span className="bl-ms-tag is-neutral">Creating form…</span> : <CheckTag>{hasOperation ? 'Form created' : 'Existing form selected'}</CheckTag>}<p>{formLabel}</p></div>
        <div className={attention ? 'is-attention' : waitingForForm || verifyingFailed ? 'is-pending' : 'is-checking'} role={attention ? 'alert' : undefined}>
          <span aria-hidden="true">{attention ? '!' : waitingForForm || verifyingFailed ? '○' : <Beacon size="inline" className="is-current" delayMs={0} slowAfterMs={null} />}</span>
          <div><strong>{attention ? 'Lead access check interrupted' : waitingForForm ? 'Verify lead access' : 'Verifying lead access'}</strong>
            <p>{attention ? (error || 'Check again to continue with this same form.') : waitingForForm ? 'Next, after the form is ready.' : 'Usually just a moment.'}</p></div>
        </div>
        <div className="is-pending"><span aria-hidden="true">○</span><strong>Save company setup</strong></div>
      </div>
      <p className="bl-ms-field-note">{verifyingFailed ? 'Your progress is saved. Reopen setup to retry this check.' : waitingForForm ? 'Form creation continues if you close this window. Reopen setup to finish verification.' : 'Verification must finish before this setup is ready.'}</p>
      {attention && !hasOperation && snapshot.pending && <button type="button" className="bl-ms-text-button" disabled={Boolean(busy)} onClick={repair}>Prepare lead access and verify</button>}
      {verifyingFailed && !hasOperation && snapshot.pending && (discardConfirm
        ? <div className="bl-ms-notice"><div><strong>Discard this proposed setup?</strong><p>{snapshot.active ? 'The current company default stays in place.' : 'You can start Meta setup again at any time.'} No campaign or Meta asset is changed.</p>
          <div className="bl-ms-inline-actions"><button type="button" className="bl-ms-text-button" disabled={Boolean(busy)} onClick={() => setDiscardConfirm(false)}>Keep it</button><button type="button" className="bl-ms-text-button bl-msp-danger" disabled={Boolean(busy)} onClick={discardCandidate}>Discard proposed setup</button></div></div></div>
        : <button type="button" className="bl-ms-text-button" disabled={Boolean(busy)} onClick={() => setDiscardConfirm(true)}>Discard proposed setup</button>)}
    </>;
  } else if (view === 'connect') {
    body = <>
      {heading(stepLabel(-1), 'Connect Meta Ads', 'Sign in to Meta to connect your Facebook Pages and ad accounts.')}
      <div className="bl-ms-external-task"><span className="bl-ms-meta-symbol" aria-hidden="true">∞</span><h3>Secure sign-in with Meta</h3><p>Allow the requested access. You’ll return here to continue setup.</p></div>
    </>;
  } else if (view === 'loading') {
    body = <>
      {heading('Meta access', 'Checking your Meta connection', 'Loading this company’s saved Meta authorization.')}
      <Beacon size="control" label="Checking your Meta connection…" delayMs={0} slowAfterMs={null} />
    </>;
  } else if (view === 'access' || view === 'check') {
    const check = view === 'check';
    body = <>
      {heading('Meta access',
        check ? 'Check your Meta access' : renewal ? 'Renew access to Meta' : 'Allow access to your leads',
        check ? (authorizationState === 'unavailable' ? 'Meta connection status could not be loaded. Reload it before continuing.' : 'Your Meta authorization needs a check before setup can continue.')
          : renewal ? 'Your Meta authorization is no longer valid. Renew it with the same Meta account to continue.'
            : 'Your Meta account is connected. One permission still needs your attention.')}
      <div className="bl-ms-asset-card"><span className="bl-ms-meta-symbol is-large" aria-hidden="true">∞</span><div><strong>{check || renewal ? 'Meta connection' : 'Meta connected'}</strong><small>{companyLabel} workspace</small></div>{!check && !renewal && <CheckTag>Connected</CheckTag>}</div>
      {!check && <div className="bl-ms-external-task"><h3>{renewal ? 'Renew access in Meta' : 'Update your permissions in Meta'}</h3><p>{renewal ? 'Sign in with the same Meta account. Your saved setup stays in place until renewal is verified.' : 'Allow BeyondLeads to access your Page’s leads, then return to continue.'}</p></div>}
      <Disclosure className="bl-ms-optional" summary="Why is this needed?"><p>This lets BeyondLeads bring new enquiries into your Leads page. Meta controls which permissions you can grant.</p></Disclosure>
    </>;
  } else if (view === 'assets') {
    const noInventory = resources && choices.accounts.length === 0;
    body = <>
      {heading(stepLabel(0), 'Choose where your ads belong', 'Select the Facebook Page and ad account for your company.')}
      {!resources ? <Beacon size="control" label="Loading your Meta assets…" delayMs={0} slowAfterMs={null} /> : noInventory ? (
        <div className="bl-ms-notice"><span className="bl-ms-notice-icon" aria-hidden="true">↻</span><div><strong>No Facebook Pages or ad accounts are available yet</strong><p>Refresh your Meta assets. If they still don’t appear, renew access with a Meta account that manages them.</p>{onRefresh && <button type="button" className="bl-ms-text-button" disabled={Boolean(busy)} onClick={() => action('refresh', async () => { await onRefresh(); await onReload(); })}>Refresh available assets</button>}</div></div>
      ) : <>
        <label className="bl-ms-field" htmlFor={`${ids}-page`}><span>Facebook Page</span>
          <Select id={`${ids}-page`} className="bl-select-block" value={editor.selection.pageResourceId} disabled={Boolean(busy) || hasOperation || !editor.selection.credentialId} onChange={(value) => editSelection('pageResourceId', value)}
            options={[
              { value: '', label: editor.selection.credentialId ? 'Choose a Facebook Page' : 'Choose an ad account first' },
              ...choices.pages.map((page) => ({ value: page.resourceId, label: page.label })),
            ]} /></label>
        <label className="bl-ms-field" htmlFor={`${ids}-account`}><span>Ad account</span>
          <Select id={`${ids}-account`} className="bl-select-block" value={editor.selection.adAccountResourceId} disabled={Boolean(busy) || hasOperation} onChange={(value) => editSelection('adAccountResourceId', value)}
            placeholder="Choose an ad account"
            options={choices.accounts.map((account) => ({ value: account.resourceId, label: `${account.label}${account.currency ? ` · ${account.currency}` : ''}`, disabled: account.selectable !== true }))} /></label>
        {editor.selection.credentialId && choices.pages.length === 0 && <p className="bl-ms-field-note">No Facebook Page from this Meta connection is available yet. Refresh your Meta assets or choose another ad account.</p>}
        <div className="bl-ms-notice"><span className="bl-ms-notice-icon" aria-hidden="true">↗</span><div>
          <strong>{choices.assigned ? `Page assigned to ${companyLabel}` : `Assign this Page to ${companyLabel}`}</strong>
          <p>{choices.assigned ? 'These assets will be used for future campaigns.' : 'Continuing lets this company use the selected Page in BeyondLeads.'}</p>
        </div></div>
      </>}
    </>;
  } else if (view === 'terms') {
    body = <>
      {heading(stepLabel(1), 'Enable leads for your Page', 'Accept Meta’s Lead Ads Terms to collect enquiries from your ads.')}
      <div className="bl-ms-asset-card"><PageAvatar label={pageLabel} /><div><strong>{pageLabel || 'Selected Facebook Page'}</strong><small>Facebook Page · Assigned to {companyLabel}</small></div></div>
      {termsAccepted
        ? <div className="bl-ms-notice is-success" role="status"><strong>Lead Ads Terms accepted</strong><p>Meta confirmed this Page’s Lead Ads Terms.</p></div>
        : <>
          <div className="bl-ms-external-task"><div className="bl-ms-external-icon" aria-hidden="true">↗</div><h3>A quick step in Meta</h3><p>Open Meta, select <strong>{pageLabel || 'this Page'}</strong>, and accept the terms. We’ll check when you return.</p><span className="bl-ms-field-note">Only a Page administrator can accept.</span></div>
          <button type="button" className="bl-ms-text-button" disabled={Boolean(busy)} onClick={checkTerms}>Already accepted? Check again</button>
        </>}
    </>;
  } else {
    const summary = <div className="bl-ms-completed-summary" aria-label="Completed setup"><span>✓ Meta connected</span><span>✓ Page assigned</span>{termsAccepted && <span>✓ Lead Ads Terms accepted</span>}</div>;
    const formTitle = replacementRequired ? 'Your lead form needs an update' : preview ? (formReuse ? 'Your existing form is ready' : 'Review, then finish setup') : 'Prepare your lead form';
    const formIntro = replacementRequired ? 'The current form uses older questions or destinations.' : preview ? (formReuse ? 'Reuse your company’s verified standard form.' : 'Check the details below before creating your lead form.') : 'A simple form for renovation enquiries, ready for you to review.';
    body = <>
      <p className="bl-ms-eyebrow">{stepLabel(2)}</p>
      <div className="bl-ms-form-grid">
        <div>
          <h2 className="bl-ms-step-title" tabIndex={-1} ref={title}>{formTitle}</h2>
          <p className="bl-ms-intro" id={`${ids}-intro`}>{formIntro}</p>
          {summary}
          {replacementRequired ? (
            <div className="bl-ms-notice"><span className="bl-ms-notice-icon" aria-hidden="true">↻</span><div><strong>A replacement form is needed</strong><p>Existing campaigns keep their current form. Review the replacement before it is created.</p></div></div>
          ) : preview ? <>
            <dl className="bl-ms-review-list">
              <div><dt>Facebook Page</dt><dd>{pageLabel || 'Saved Facebook Page'}</dd></div>
              <div><dt>Ad account</dt><dd>{selectedAccount?.label || snapshot.pending?.destination?.adAccount?.label || 'Saved ad account'}{selectedAccount?.currency ? ` · ${selectedAccount.currency}` : ''}</dd></div>
              <div><dt>Form</dt><dd>{preview.formName} <span className="bl-ms-tag is-neutral">{formReuse ? 'Reuse existing' : 'Create new'}</span></dd></div>
              <div><dt>Privacy policy</dt><dd className="bl-ms-url-text">{editor.privacyPolicyUrl}</dd></div>
              <div><dt>After submission</dt><dd className="bl-ms-url-text">{editor.followUpUrl}</dd></div>
            </dl>
            <button type="button" className="bl-ms-text-button" disabled={Boolean(busy)} onClick={() => { setPreview(null); setExistingForm(null); }}>Edit form destinations</button>
            <p className="bl-ms-approval-note">{formReuse ? 'Saves these assets for future campaigns.' : 'Creates this form in Meta and saves these assets for future campaigns.'} No ads will launch.</p>
            {existingCampaigns > 0 && setupChanged && <div className="bl-ms-notice"><div><strong>Existing campaigns keep their saved assets</strong><p>This setup becomes the default for future campaigns.</p></div></div>}
          </> : <>
            <div className="bl-ms-form-intro"><span className="bl-ms-document-icon" aria-hidden="true">≡</span><div><strong>BeyondLeads standard form</strong><p>Project timing, name, email and phone.</p></div></div>
            <div className="bl-ms-fields">
              <label className="bl-ms-field" htmlFor={`${ids}-privacy`}><span>Privacy policy URL</span><input id={`${ids}-privacy`} type="url" value={editor.privacyPolicyUrl} placeholder="https://yourcompany.com/privacy" aria-describedby={`${ids}-url-note`} disabled={Boolean(busy) || hasOperation} onChange={(event) => editUrl('privacyPolicyUrl', event.target.value)} /></label>
              <label className="bl-ms-field" htmlFor={`${ids}-followup`}><span>Website after submission</span><input id={`${ids}-followup`} type="url" value={editor.followUpUrl} placeholder="https://yourcompany.com" aria-describedby={`${ids}-url-note`} disabled={Boolean(busy) || hasOperation} onChange={(event) => editUrl('followUpUrl', event.target.value)} /></label>
            </div>
            <p id={`${ids}-url-note`} className="bl-ms-field-note">From your company profile. Changes are saved when you choose Review form.</p>
            <Disclosure className="bl-ms-optional" summary="What can I change?"><p>You can update the privacy policy and follow-up website. The form questions stay consistent across your campaigns.</p></Disclosure>
          </>}
        </div>
        <MetaInstantFormPreview pageLabel={pageLabel} preview={preview} />
      </div>
    </>;
  }

  const completion = META_SETUP_COMPLETION[entry] || META_SETUP_COMPLETION.company;
  const termsUrl = safeMetaTermsUrl(terms?.termsUrl);
  let primary = null;
  if (view === 'success') primary = { label: completion.cta, onClick: () => finish({ advance: true }) };
  else if (view === 'verifying') primary = verifyingFailed ? { label: 'Check again', onClick: checkAgain } : null;
  else if (view === 'connect') primary = { label: 'Connect Meta Ads', onClick: connect };
  else if (view === 'loading') primary = { label: 'Continue', disabled: true };
  else if (view === 'access') primary = { label: renewal ? 'Renew access in Meta' : 'Update permissions in Meta', onClick: connect };
  else if (view === 'check') primary = authorizationState === 'unavailable'
    ? { label: 'Reload connection status', onClick: () => metaConnection?.reload?.() }
    : { label: authorizationState === 'temporarily_unavailable' ? 'Check again' : 'Check authorization', onClick: () => action('check', onCheckAuthorization), disabled: !onCheckAuthorization || authorizationState === 'checking' };
  else if (view === 'assets') primary = { label: choices.assigned ? 'Continue' : 'Assign Page & continue', onClick: continueFromAssets, disabled: !selectedAccount || !selectedPage || hasOperation };
  else if (view === 'terms') primary = termsAccepted ? { label: 'Continue to form', onClick: () => setStage(2) }
    : termsUrl ? { label: 'Open Meta to accept terms', href: termsUrl, onClick: () => { termsHandoff.current = true; } }
      : { label: 'Check again', onClick: checkTerms };
  else if (replacementRequired) primary = { label: 'Review a replacement form', onClick: () => reviewForm({ replacement: true }) };
  else if (preview) primary = { label: formReuse ? 'Save setup' : 'Create form & finish', onClick: confirmSetup, disabled: !reviewValid };
  else primary = { label: 'Review form', onClick: () => reviewForm() };

  const showBack = ['assets', 'terms', 'form'].includes(view) && (stage > 0 || reviewed || replacementRequired);
  const busyLabel = META_SETUP_BUSY_LABELS[busy] || (busy ? 'Saving progress…' : '');
  const displaySteps = [...(startedDisconnected ? [{ label: 'Connect Meta', description: 'Secure sign-in', index: -1 }] : []), ...STEPS.map((step, index) => ({ ...step, index }))];
  const railStage = stage === 3 ? 2 : stage;

  const overlay = <div className="bl-ms-overlay" role="presentation">
    <section ref={dialog} className="bl-ms-wizard" role="dialog" aria-modal="true" aria-labelledby={`${ids}-title`} aria-describedby={`${ids}-intro`} inert={changeConfirmOpen ? '' : undefined}>
      <header className="bl-ms-header">
        <div className="bl-ms-heading"><span className="bl-ms-meta-icon" aria-hidden="true">∞</span><div><div className="bl-ms-context">{companyLabel}</div><h2 id={`${ids}-title`}>Set up Meta assets</h2></div></div>
        <button type="button" className="bl-ms-icon-button" aria-label="Close setup" disabled={Boolean(busy)} onClick={close}><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18" /></svg></button>
      </header>
      <aside className="bl-ms-rail" aria-label="Setup progress">
        <ol className="bl-ms-steps" style={{ '--step-count': displaySteps.length }}>
          {displaySteps.map(({ label, description, index }, order) => {
            const completed = pendingReady || (index === -1 ? authorizationUsable
              : index === 0 ? choices.assigned || stage === 3
                : index === 1 ? termsAccepted || stage === 3 : false);
            const isCurrent = !pendingReady && (index === -1 ? view === 'connect' : view !== 'connect' && railStage === index);
            return <li key={label} className={[completed && 'is-complete', isCurrent && 'is-current'].filter(Boolean).join(' ') || undefined} aria-current={isCurrent ? 'step' : undefined}>
              <button type="button" disabled={Boolean(busy) || index < 0 || !connected || stage === 3 || pendingReady || index >= stage} onClick={() => goToStep(index)}>
                <span className="bl-ms-step-number">{completed ? '✓' : order + 1}</span>
                <span><strong>{label}</strong><small>{completed ? 'Complete' : isCurrent ? 'Current step' : description}</small></span>
              </button>
            </li>;
          })}
        </ol>
      </aside>
      <main ref={content} className="bl-ms-content" aria-busy={Boolean(busy)}>
        {!canManage && <p className="bl-ms-error" role="alert">A company owner or admin must complete Meta setup.</p>}
        {body}
        {/* The access and interrupted-check views already present the failure. */}
        {error && view !== 'access' && !(view === 'verifying' && verifyingFailed) && <div className="bl-ms-error" role="alert">{error}</div>}
        {error && view === 'access' && <p className="bl-ms-sr-only" role="alert">{error}</p>}
      </main>
      <footer className={`bl-ms-footer${view === 'verifying' ? ' is-verifying' : ''}`}>
        {showBack && <button type="button" className="bl-ms-text-button bl-ms-back" disabled={Boolean(busy)} onClick={back}><span aria-hidden="true">←</span> Back</button>}
        <div className="bl-ms-footer-actions">
          {view !== 'success' && <button type="button" className="bl-ms-button is-secondary bl-ms-save-close" disabled={Boolean(busy)} onClick={close}>{view === 'verifying' ? 'Close for now' : 'Save & close'}</button>}
          {primary && (primary.href && !busy
            ? <a className="bl-ms-button is-primary bl-ms-next" href={primary.href} target="_blank" rel="noreferrer" onClick={primary.onClick}>{primary.label}</a>
            : <button type="button" className="bl-ms-button is-primary bl-ms-next" disabled={Boolean(busy) || primary.disabled || !canManage} aria-busy={busy ? 'true' : undefined} onClick={primary.onClick}>
              {busy ? <><Beacon size="inline" className="is-current" delayMs={0} slowAfterMs={null} /> {busyLabel}</> : primary.label}
            </button>)}
        </div>
      </footer>
    </section>
    {changeConfirmOpen && <div className="bl-ms-confirm-backdrop" role="presentation">
      <section ref={confirmBox} className="bl-ms-confirm" role="alertdialog" aria-modal="true" aria-labelledby={`${ids}-change-title`} aria-describedby={`${ids}-change-description`}>
        <span className="bl-ms-confirm-icon" aria-hidden="true">!</span>
        <h2 id={`${ids}-change-title`} tabIndex={-1}>Change setup for future campaigns?</h2>
        <p id={`${ids}-change-description`}>{existingCampaigns > 0
          ? <><strong>{existingCampaigns} existing campaign{existingCampaigns === 1 ? '' : 's'}</strong> {existingCampaigns === 1 ? 'keeps its' : 'keep their'} saved Page, ad account and lead form.</>
          : <><strong>Existing campaigns</strong> keep their saved Page, ad account and lead form.</>} Your changes become the default for future campaigns.</p>
        <p className="bl-ms-confirm-note">Keep access to the existing assets in Meta so lead and campaign updates can continue.</p>
        <div className="bl-ms-confirm-actions"><button type="button" className="bl-ms-button is-secondary" onClick={keepCurrentSetup}>Keep current setup</button><button type="button" className="bl-ms-button is-primary" onClick={continueChange}>Continue to change setup</button></div>
      </section>
    </div>}
    <div className="bl-ms-sr-only" role="status" aria-live="polite" aria-atomic="true">{announcement}</div>
  </div>;
  return typeof document === 'undefined' ? overlay : createPortal(overlay, document.body);
}
